import { Client } from '@stomp/stompjs'
import { action, makeObservable, observable } from 'mobx'
import LocalUser from './user/local'
import RemoteUser from './user/remote'

export type Constraints = { audio: boolean, video: boolean }

class MediaServerPeer<T> {

  private client: Client | null = null
  users = observable.map<string, RemoteUser<T>>([])
  currentUser: LocalUser<T> | null = null

  status: "not-connected" | "connecting" | "connected" = "not-connected"

  constructor() {
    makeObservable(this, {
      currentUser: observable.ref,
      status: observable,
      setStatus: action,
      addUser: action,
      removeUser: action,
      disconnect: action
    })
  }

  connect(wsUrl: string, token: string) {
    const client = new Client({ brokerURL: wsUrl })
    this.client = client
    client.heartbeatOutgoing = 5000
    client.connectHeaders = { token }
    client.activate()

    client.onConnect = this.onStompConnect.bind(this)
    this.setStatus("connecting")
  }

  disconnect() {
    this.client?.deactivate()
    this.users.clear()
  }

  setStatus (status: typeof this.status) {
    this.status = status
  }

  async setConstraints(newConstraints: Partial<Constraints>, streamName: string) {
    if (!this.currentUser) return
    const resp = await this.currentUser.changeConstraints(newConstraints, streamName)
    if (!resp) return
    
    if (resp.constraints.audio === false && resp.constraints.video === false) {
      this.currentUser.closeStream(streamName)
      this.send("/stopBroadcast", { streamName })
      return
    }
    this.send("/broadcast", { ...resp, streamName })
  }

  async toggleConstraint(constraintsName: "audio" | "video", streamName: string = "") {
    if (!this.currentUser) return

    this.setConstraints({ [ constraintsName ]: !this.getConstraint(constraintsName, streamName) }, streamName)
  }

  getConstraint(constraintsName: "audio" | "video", streamName: string = "") {
    if (!this.currentUser) return false
    return !!this.currentUser.streams[streamName]?.constraints[constraintsName]
  }

  private onStompConnect() {
    if (!this.client) return
    
    this.setStatus("connected")
    this.client.subscribe("/room", message => {
      const data = JSON.parse(message.body)
      if (data.type === "userConnected") {
        return this.addUser(data.userId, data.userInfo)
      }
      if (data.type === "userLeaved") {
        return this.removeUser(data.userId)
      }
      if (data.type === "userBroadcast") {
        return this.userBroadcast(data.userId, data.offer, data.constraints, data.streamName)
      }
      if (data.type === "userStopBroadcast") {
        return this.userStopBroadcast(data.userId, data.streamName)
      }
      if (data.type === "consumeBroadcast") {
        this.currentUser?.consume(data.answer, data.streamName)
      }
    })
  }

  addUser(userId: string, userInfo: T) {
    const user = new RemoteUser(userInfo)
    this.users.set(userId, user)
  }

  removeUser(userId: string) {
    const user = this.users.get(userId)
    if (!user) return
    user.dispose()
    this.users.delete(userId)
  }
    
  private async userBroadcast(userId: string, offer: any, constraints: any, streamName: string) {
    const user = this.users.get(userId)
    if (!user) return
    
    const answer = await user.produce(offer, constraints, streamName)
    if (answer) {
      this.send("/consumeBroadcast", { userId, answer, streamName })
    }
  }

  private async userStopBroadcast(userId: string, streamName: string) {
    const user = this.users.get(userId)
    if (!user) return

    user.stopProduce(streamName)
  }

  private send(destination: string, body: any) {
    this.client?.publish({ 
      destination, 
      body: JSON.stringify(body), 
      headers: { "content-type": "application/json" } 
    })
  }

}

export default MediaServerPeer